import IProcedureDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureDetail';
import IProcedureActionDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureActionDetail';
import IProcedureActionOverlayDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureActionOverlayDetail';
import OverlayType from '@Shared.Angular/@types/core/contracts/queryModel/procedure/overlayType';
import { Guid } from '@Shared.Angular/@types/guid';
import { Services } from '@Shared.Angular/@types/services';

type SanitizeHtml = (html: string) => string;

const escapeHtml = (content: string): string => {
  return content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const escapeAttribute = (content: string): string => {
  return content.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
};

const getRenderedActionImageSource = (actionId: Guid): string | null => {
  const actionElement = document.getElementById(`action_${actionId}`);
  if (!actionElement) {
    return null;
  }

  const imageElement = actionElement.querySelector(
    '.procedure-action-image img'
  ) as HTMLImageElement | null;

  return imageElement?.src || null;
};

const getFileImageSource = async (
  fileService: Services.FileService,
  fileId?: Guid
): Promise<string | null> => {
  if (!fileId) {
    return null;
  }

  try {
    const fileResponse = await fileService.getFileById(fileId);
    if (!fileResponse?.data) {
      return null;
    }

    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(fileResponse.data);
    });
  } catch {
    return null;
  }
};

const loadImage = async (imageSource: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject();
    image.src = imageSource;
  });
};

const normalizeOverlay = (
  overlay: IProcedureActionOverlayDetail,
  imageWidth: number,
  imageHeight: number
) => {
  const x = Math.max(0, Math.min(overlay.x, imageWidth));
  const y = Math.max(0, Math.min(overlay.y, imageHeight));
  const width = Math.max(0, Math.min(overlay.width, imageWidth - x));
  const height = Math.max(0, Math.min(overlay.height, imageHeight - y));

  return {
    x,
    y,
    width,
    height
  };
};

const compositeImageWithOverlays = async (
  imageSource: string,
  overlays: IProcedureActionOverlayDetail[]
): Promise<string> => {
  const highlightOverlays = overlays.filter(
    (overlay) => overlay.type === OverlayType.Highlight
  );

  try {
    const baseImage = await loadImage(imageSource);
    const canvas = document.createElement('canvas');
    canvas.width = baseImage.naturalWidth;
    canvas.height = baseImage.naturalHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      return imageSource;
    }

    context.drawImage(baseImage, 0, 0);

    if (highlightOverlays.length > 0) {
      context.fillStyle = 'rgba(0, 0, 0, 0.5)';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    const borderWidth = Math.max(
      4,
      Math.round(Math.min(canvas.width, canvas.height) * 0.005)
    );

    highlightOverlays.forEach((overlay) => {
      const normalizedOverlay = normalizeOverlay(
        overlay,
        canvas.width,
        canvas.height
      );

      if (!normalizedOverlay.width || !normalizedOverlay.height) {
        return;
      }

      context.drawImage(
        baseImage,
        normalizedOverlay.x,
        normalizedOverlay.y,
        normalizedOverlay.width,
        normalizedOverlay.height,
        normalizedOverlay.x,
        normalizedOverlay.y,
        normalizedOverlay.width,
        normalizedOverlay.height
      );

      context.lineWidth = borderWidth;
      context.strokeStyle = '#f2c94c';
      context.strokeRect(
        normalizedOverlay.x,
        normalizedOverlay.y,
        normalizedOverlay.width,
        normalizedOverlay.height
      );
    });

    return canvas.toDataURL('image/png');
  } catch {
    return imageSource;
  }
};

const getClipboardActionImageSource = async (
  action: IProcedureActionDetail,
  fileService: Services.FileService
): Promise<string | null> => {
  const renderedImageSource = getRenderedActionImageSource(action.id);
  if (renderedImageSource) {
    return compositeImageWithOverlays(
      renderedImageSource,
      action.overlays || []
    );
  }

  const storedImageSource = await getFileImageSource(
    fileService,
    action.modifiedFileId || action.originalFileId
  );
  const baseImageSource = storedImageSource;

  if (!baseImageSource) {
    return null;
  }

  return compositeImageWithOverlays(baseImageSource, action.overlays || []);
};

const mapWithConcurrency = async <TInput, TOutput>(
  items: TInput[],
  maxConcurrency: number,
  mapper: (item: TInput, index: number) => Promise<TOutput>
): Promise<TOutput[]> => {
  const results: TOutput[] = new Array(items.length);
  let nextIndex = 0;

  const worker = async (): Promise<void> => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  };

  const workers = Array.from(
    { length: Math.min(maxConcurrency, items.length) },
    () => worker()
  );

  await Promise.all(workers);
  return results;
};

const buildProcedureClipboardHtml = async (
  procedure: IProcedureDetail,
  fileService: Services.FileService,
  sanitizeHtml: SanitizeHtml
): Promise<string> => {
  const actionSections = await mapWithConcurrency(
    procedure.actions,
    4,
    async (action, index) => {
      const imageSource = await getClipboardActionImageSource(
        action,
        fileService
      );
      const actionTitle = action.title?.trim()
        ? action.title
        : `Step ${index + 1}`;
      const sanitizedActionDescription = action.description
        ? sanitizeHtml(action.description)
        : '';
      const actionDescription = action.description
        ? `<div>${sanitizedActionDescription}</div>`
        : '';
      const actionImage = imageSource
        ? `<div style="margin-top: 8px;"><img src="${escapeAttribute(
            imageSource
          )}" alt="Step ${
            index + 1
          } image" style="display:block; max-width: 100%; height: auto;" /></div>`
        : '';

      return `<section style="margin-bottom: 20px;"><h3 style="margin: 0 0 8px 0;">${
        index + 1
      }. ${escapeHtml(
        actionTitle
      )}</h3>${actionDescription}${actionImage}</section>`;
    }
  );

  const sanitizedProcedureDescription = procedure.description
    ? sanitizeHtml(procedure.description)
    : '';
  const procedureDescription = procedure.description
    ? `<div>${sanitizedProcedureDescription}</div>`
    : '';
  const recordingLink = window.location.href;

  return `<article><h1 style="margin: 0 0 12px 0;">${escapeHtml(
    procedure.name
  )}</h1>${procedureDescription}<hr style="margin: 16px 0;" />${actionSections.join(
    ''
  )}<hr style="margin: 16px 0;" /><p style="margin: 0;"><strong>Recording link:</strong> <a href="${escapeAttribute(
    recordingLink
  )}">${escapeHtml(recordingLink)}</a></p></article>`;
};

export const copyProcedureToClipboard = async (
  procedure: IProcedureDetail,
  fileService: Services.FileService,
  sanitizeHtml: SanitizeHtml
): Promise<void> => {
  const htmlContent = await buildProcedureClipboardHtml(
    procedure,
    fileService,
    sanitizeHtml
  );
  const inertDocument = new DOMParser().parseFromString(
    htmlContent,
    'text/html'
  );
  const plainTextContent = inertDocument.body.textContent || '';

  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([plainTextContent], { type: 'text/plain' })
      })
    ]);
    return;
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(plainTextContent);
    return;
  }

  throw new Error('Clipboard API is not available in this browser context.');
};
